import { SUBSCRIPTION_LIFECYCLES } from './subscriptionStatus.js';

export const normalizeBillingCycle = (cycle) => {
  const normalized = String(cycle || '').toLowerCase();
  return normalized === 'yearly' ? 'annual' : normalized || 'monthly';
};

export const getPlanPrice = (plan, frequency) => {
  const normalizedFrequency = normalizeBillingCycle(frequency);
  return plan?.prices?.find((price) => normalizeBillingCycle(price.frequency) === normalizedFrequency) || null;
};

export const isOneTimePlan = (plan) => (
  normalizeBillingCycle(plan?.billing_cycle) === 'one-time'
  || plan?.prices?.some((price) => price.frequency === 'one-time')
);

export const isFreePlan = (plan) => {
  const monthlyPrice = getPlanPrice(plan, 'monthly');
  return plan?.plan_id === 'plan_free' || monthlyPrice?.amount === 0;
};

export const getCurrentPlanId = (plans, responseCurrentPlanId) => (
  responseCurrentPlanId
  || plans.find((plan) => plan.is_current_plan)?.plan_id
  || 'plan_free'
);

export const sortPlansByRegularPrice = (plans, frequency) => [...plans].sort((left, right) => {
  const leftAmount = getPlanPrice(left, frequency)?.amount ?? Number.POSITIVE_INFINITY;
  const rightAmount = getPlanPrice(right, frequency)?.amount ?? Number.POSITIVE_INFINITY;
  return leftAmount - rightAmount;
});

export const getPlanVariantState = ({
  targetPlan,
  currentPlan,
  selectedFrequency,
  currentFrequency,
  subscriptionLifecycle,
}) => {
  const hasCurrentPaidTier = [
    SUBSCRIPTION_LIFECYCLES.ACTIVE,
    SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED,
    SUBSCRIPTION_LIFECYCLES.PAST_DUE,
  ].includes(subscriptionLifecycle);
  const isCurrentTier = hasCurrentPaidTier && targetPlan?.plan_id === currentPlan?.plan_id;
  const isCurrentVariant = isCurrentTier
    && normalizeBillingCycle(selectedFrequency) === normalizeBillingCycle(currentFrequency);

  return { isCurrentTier, isCurrentVariant };
};

export const resolvePlanAction = ({
  targetPlan,
  currentPlan,
  selectedFrequency,
  currentFrequency,
  hasActiveSubscription,
  subscriptionLifecycle,
}) => {
  const frequency = normalizeBillingCycle(selectedFrequency);
  const activeFrequency = normalizeBillingCycle(currentFrequency);
  const lifecycle = subscriptionLifecycle || (hasActiveSubscription
    ? SUBSCRIPTION_LIFECYCLES.ACTIVE
    : SUBSCRIPTION_LIFECYCLES.FREE);

  if (isOneTimePlan(targetPlan)) {
    return { type: 'purchase-pass', label: `Buy ${targetPlan.name}`, loadingLabel: 'Processing...' };
  }

  if ([SUBSCRIPTION_LIFECYCLES.PAST_DUE, SUBSCRIPTION_LIFECYCLES.UNKNOWN].includes(lifecycle)) {
    return { type: 'unavailable', label: 'Contact Support', loadingLabel: 'Unavailable' };
  }

  const selectedPrice = getPlanPrice(targetPlan, frequency);
  if (!selectedPrice) {
    return { type: 'unavailable', label: 'Unavailable', loadingLabel: 'Unavailable' };
  }

  if ([SUBSCRIPTION_LIFECYCLES.FREE, SUBSCRIPTION_LIFECYCLES.EXPIRED].includes(lifecycle)
    || !currentPlan
    || isFreePlan(currentPlan)) {
    return {
      type: 'checkout',
      direction: 'upgrade',
      label: `Upgrade to ${targetPlan.name}`,
      loadingLabel: 'Preparing checkout...',
    };
  }

  if (targetPlan.plan_id === currentPlan.plan_id) {
    if (frequency === activeFrequency) {
      const label = lifecycle === SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED
        ? 'Cancellation Scheduled'
        : 'Current Plan';
      return { type: 'current', label, loadingLabel: label };
    }

    if (lifecycle === SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED) {
      return { type: 'unavailable', label: 'Cancellation Scheduled', loadingLabel: 'Unavailable' };
    }

    const switchingToAnnual = frequency === 'annual';
    return {
      type: switchingToAnnual ? 'upgrade' : 'downgrade',
      direction: 'switch',
      label: `Switch to ${switchingToAnnual ? 'Annual' : 'Monthly'}`,
      loadingLabel: switchingToAnnual ? 'Preparing switch...' : 'Scheduling switch...',
    };
  }

  const targetAmount = selectedPrice.amount;
  const currentAmount = getPlanPrice(currentPlan, frequency)?.amount;
  if (!Number.isFinite(targetAmount) || !Number.isFinite(currentAmount)) {
    return { type: 'unavailable', label: 'Unavailable', loadingLabel: 'Unavailable' };
  }

  if (targetAmount > currentAmount) {
    return {
      type: 'upgrade',
      direction: 'upgrade',
      label: `Upgrade to ${targetPlan.name}`,
      loadingLabel: 'Upgrading...',
    };
  }

  if (targetAmount < currentAmount) {
    if (lifecycle === SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED) {
      return { type: 'unavailable', label: 'Cancellation Scheduled', loadingLabel: 'Unavailable' };
    }
    return {
      type: 'downgrade',
      direction: 'downgrade',
      label: `Downgrade to ${targetPlan.name}`,
      loadingLabel: 'Scheduling downgrade...',
    };
  }

  return { type: 'unavailable', label: 'Contact Support', loadingLabel: 'Unavailable' };
};
